import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Mail,
  Search,
  ArrowLeft,
  Trash2,
  Loader2,
  Copy,
  Check,
  FileText,
  ListChecks,
  Reply,
  ChevronRight,
  Inbox,
  RefreshCw,
  Menu,
  PenLine
} from 'lucide-react';
import DraftModal from '../components/modals/DraftModal';
import ActionsPanel from '../components/panels/ActionsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailsPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // State
  const [inboxEmail, setInboxEmail] = useState('');
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadEmails, setThreadEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  
  // AI Actions state
  const [aiAction, setAiAction] = useState(null); // 'draft' | 'summarize' | 'actions'
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  
  // Fetch inbox email address
  const fetchInboxEmail = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/emails/address`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInboxEmail(data.inbox_email);
      }
    } catch (error) {
      console.error('Failed to fetch inbox email:', error);
    }
  }, [token]);
  
  // Fetch threads
  const fetchThreads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/emails/threads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setThreads(data.threads);
      }
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  // Fetch single thread with emails
  const fetchThread = useCallback(async (threadId) => {
    try {
      setLoadingThread(true);
      setAiResult(null);
      const res = await fetch(`${API}/api/emails/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedThread(data.thread);
        setThreadEmails(data.emails);
      }
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    } finally {
      setLoadingThread(false);
    }
  }, [token]);
  
  // Search emails
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    
    try {
      setSearching(true);
      const res = await fetch(
        `${API}/api/emails/search?q=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.threads);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, [token, searchQuery]);
  
  // Delete thread
  const deleteThread = async (threadId) => {
    if (!window.confirm('Delete this email thread?')) return;
    
    try {
      const res = await fetch(`${API}/api/emails/threads/${threadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setThreads(prev => prev.filter(t => t.id !== threadId));
        if (selectedThread?.id === threadId) {
          setSelectedThread(null);
          setThreadEmails([]);
        }
      }
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };
  
  // AI Actions
  const handleAiAction = async (action) => {
    if (!selectedThread) return;
    
    const endpoints = {
      draft: 'draft-reply',
      summarize: 'summarize',
      actions: 'extract-actions'
    };
    
    try {
      setAiLoading(true);
      setAiAction(action);
      setAiResult(null);
      
      const res = await fetch(
        `${API}/api/emails/threads/${selectedThread.id}/${endpoints[action]}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        setAiResult(data.draft || data.summary || data.action_items);
      } else {
        const error = await res.json();
        setAiResult(`Error: ${error.detail || 'Failed to process'}`);
      }
    } catch (error) {
      console.error('AI action failed:', error);
      setAiResult('Error: Failed to connect to AI service');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Initial load
  useEffect(() => {
    fetchInboxEmail();
    fetchThreads();
  }, [fetchInboxEmail, fetchThreads]);
  
  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) handleSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);
  
  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };
  
  const displayThreads = searchResults !== null ? searchResults : threads;
  
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            {/* Backdrop for mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-20 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
            
            <motion.aside
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed lg:relative left-0 top-0 bottom-0 w-80 bg-card border-r border-border z-30 flex flex-col"
            >
              {/* Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/chat')}
                    className="gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Chat
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={fetchThreads}
                    title="Refresh"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-500" />
                  Emails
                </h1>
                
                {/* Inbox Email Address */}
                {inboxEmail && (
                  <div className="mt-3 p-2 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Your Arlo email address:</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-background px-2 py-1 rounded flex-1 truncate">
                        {inboxEmail}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(inboxEmail)}
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      CC this address on emails to bring them into Arlo
                    </p>
                  </div>
                )}
              </div>
              
              {/* Search */}
              <div className="p-3 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search emails..."
                    className="pl-8 h-9"
                    data-testid="email-search-input"
                  />
                  {searching && (
                    <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                  )}
                </div>
                {searchResults !== null && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted-foreground">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults(null);
                      }}
                      className="h-6 text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Thread List */}
              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : displayThreads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <Inbox className="h-12 w-12 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {searchResults !== null ? 'No emails match your search' : 'No emails yet'}
                    </p>
                    {searchResults === null && (
                      <p className="text-xs text-muted-foreground mt-1">
                        CC your Arlo email address on conversations to see them here
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {displayThreads.map((thread) => (
                      <button
                        key={thread.id}
                        onClick={() => fetchThread(thread.id)}
                        className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${
                          selectedThread?.id === thread.id ? 'bg-muted' : ''
                        }`}
                        data-testid={`email-thread-${thread.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">
                                {thread.last_sender || thread.last_sender_email?.split('@')[0]?.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                              </p>
                              {thread.message_count > 1 && (
                                <span className="flex-shrink-0 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-medium bg-primary/10 text-primary rounded-full">
                                  {thread.message_count}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {thread.last_sender_email || ''}
                            </p>
                            <p className="text-sm font-medium truncate mt-1">{thread.subject}</p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {thread.snippet}
                            </p>
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0">
                            <span className="text-[10px] text-muted-foreground">
                              {formatDate(thread.last_message_at)}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <div className="lg:hidden p-3 border-b border-border flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSidebar(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-medium">
            {selectedThread?.subject || 'Select an email'}
          </span>
        </div>
        
        {/* Thread View */}
        {selectedThread ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Thread Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold truncate">{selectedThread.subject}</h2>
                  <p className="text-sm text-muted-foreground">
                    {selectedThread.message_count} message{selectedThread.message_count !== 1 ? 's' : ''}
                    {selectedThread.participants?.length > 0 && (
                      <span> • {selectedThread.participants.length} participant{selectedThread.participants.length !== 1 ? 's' : ''}</span>
                    )}
                  </p>
                  {/* Participants Summary */}
                  {selectedThread.participants?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedThread.participants.slice(0, 5).map((participant, i) => (
                        <span 
                          key={i}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted text-xs rounded-full"
                          title={participant}
                        >
                          <span className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-medium text-primary">
                            {(participant.split('@')[0]?.[0] || '?').toUpperCase()}
                          </span>
                          <span className="max-w-[120px] truncate">
                            {participant.split('@')[0]?.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || participant}
                          </span>
                        </span>
                      ))}
                      {selectedThread.participants.length > 5 && (
                        <span className="inline-flex items-center px-2 py-0.5 bg-muted text-xs rounded-full text-muted-foreground">
                          +{selectedThread.participants.length - 5} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteThread(selectedThread.id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              {/* AI Action Buttons */}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDraftModal(true)}
                  className="gap-2"
                  data-testid="smart-draft-btn"
                >
                  <PenLine className="h-4 w-4" />
                  Smart Draft
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAiAction('draft')}
                  disabled={aiLoading}
                  className="gap-2"
                  data-testid="draft-reply-btn"
                >
                  <Reply className="h-4 w-4" />
                  Quick Reply
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAiAction('summarize')}
                  disabled={aiLoading}
                  className="gap-2"
                  data-testid="summarize-btn"
                >
                  <FileText className="h-4 w-4" />
                  Summarize
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAiAction('actions')}
                  disabled={aiLoading}
                  className="gap-2"
                  data-testid="extract-actions-btn"
                >
                  <ListChecks className="h-4 w-4" />
                  Extract Actions
                </Button>
                <Button
                  variant={showActionsPanel ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowActionsPanel(!showActionsPanel)}
                  className="gap-2"
                  data-testid="actions-panel-btn"
                >
                  <ListChecks className="h-4 w-4" />
                  {showActionsPanel ? 'Hide Panel' : 'Actions Panel'}
                </Button>
              </div>
            </div>

            {/* Actions Panel */}
            {showActionsPanel && selectedThread && (
              <div className="p-4 border-b border-border">
                <ActionsPanel
                  sourceType="email"
                  sourceId={selectedThread.id}
                />
              </div>
            )}
            
            {/* AI Result Panel */}
            <AnimatePresence>
              {(aiLoading || aiResult) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-border bg-muted/30 overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        {aiAction === 'draft' && <Reply className="h-4 w-4 text-emerald-500" />}
                        {aiAction === 'summarize' && <FileText className="h-4 w-4 text-blue-500" />}
                        {aiAction === 'actions' && <ListChecks className="h-4 w-4 text-violet-500" />}
                        {aiAction === 'draft' && 'Draft Reply'}
                        {aiAction === 'summarize' && 'Summary'}
                        {aiAction === 'actions' && 'Action Items'}
                      </span>
                      {aiResult && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(aiResult)}
                          className="gap-2"
                        >
                          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          Copy
                        </Button>
                      )}
                    </div>
                    
                    {aiLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </div>
                    ) : (
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <pre className="whitespace-pre-wrap text-sm bg-background p-3 rounded-lg border">
                          {aiResult}
                        </pre>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Email Messages */}
            <ScrollArea className="flex-1">
              {loadingThread ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {threadEmails.map((email, index) => {
                    // Helper to get display name from email
                    const getDisplayName = (name, emailAddr) => {
                      if (name) return name;
                      if (!emailAddr) return 'Unknown';
                      const localPart = emailAddr.split('@')[0];
                      return localPart.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    };
                    
                    const displayName = getDisplayName(email.from_name, email.from_email);
                    
                    // Helper to format email addresses
                    const formatAddressList = (addresses) => {
                      if (!addresses || addresses.length === 0) return null;
                      return addresses.map(addr => {
                        if (typeof addr === 'string') return addr;
                        return addr.email || addr.address || addr;
                      }).join(', ');
                    };
                    
                    const toList = formatAddressList(email.to);
                    const ccList = formatAddressList(email.cc);
                    
                    return (
                      <div key={email.id} className="p-4">
                        {/* Email Header */}
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-medium text-primary">
                                {displayName[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm">
                                {displayName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {email.from_email}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {new Date(email.received_at).toLocaleString()}
                          </span>
                        </div>
                        
                        {/* To/CC Recipients */}
                        <div className="pl-[52px] mb-3 space-y-1">
                          {toList && (
                            <div className="flex items-start gap-2 text-xs">
                              <span className="text-muted-foreground font-medium min-w-[24px]">To:</span>
                              <span className="text-muted-foreground break-all">{toList}</span>
                            </div>
                          )}
                          {ccList && (
                            <div className="flex items-start gap-2 text-xs">
                              <span className="text-muted-foreground font-medium min-w-[24px]">Cc:</span>
                              <span className="text-muted-foreground break-all">{ccList}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Email Body */}
                        <div className="prose prose-sm dark:prose-invert max-w-none pl-[52px]">
                          {email.text_body ? (
                            <div className="whitespace-pre-wrap font-sans text-sm">
                              {email.text_body}
                            </div>
                          ) : email.html_body ? (
                            <div 
                              className="text-sm [&>*]:my-2"
                              dangerouslySetInnerHTML={{ __html: email.html_body }}
                            />
                          ) : (
                            <p className="text-muted-foreground italic">No content</p>
                          )}
                        </div>
                        
                        {/* Attachments */}
                        {email.attachments_metadata?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2 pl-[52px]">
                            {email.attachments_metadata.map((att, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-2 bg-muted px-2 py-1 rounded text-xs"
                              >
                                <FileText className="h-3 w-3" />
                                {att.filename}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Separator for multiple emails */}
                        {index < threadEmails.length - 1 && (
                          <div className="flex items-center gap-2 mt-4 pl-[52px]">
                            <div className="flex-1 border-t border-border" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        ) : (
          /* Empty State */
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <Mail className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Select an email thread</h2>
            <p className="text-muted-foreground max-w-md">
              Choose a thread from the list to view the conversation and use Arlo to draft replies,
              summarize content, or extract action items.
            </p>
          </div>
        )}
      </main>

      {/* Draft Modal */}
      <DraftModal
        open={showDraftModal}
        onOpenChange={setShowDraftModal}
        sourceType="email"
        sourceId={selectedThread?.id}
        onUseDraft={(draft) => {
          setAiResult(draft);
          setAiAction('draft');
        }}
      />
    </div>
  );
}
